
import React, { createContext, useState, useContext, useEffect, ReactNode } from "react";

type Language = "en" | "ar";

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// Translations
const translations = {
  en: {
    // Navigation
    "nav.home": "Home",
    "nav.about": "About",
    "nav.sdgs": "SDGs",
    "nav.stories": "Stories",
    "nav.media": "Media",
    "nav.journey": "Our Journey",
    "nav.contact": "Contact",
    
    // Home page
    "home.hero.title": "Sustainability Stories from Palestine",
    "home.hero.subtitle": "Amplifying Palestinian resilience through the lens of the Sustainable Development Goals",
    "home.hero.cta": "Explore SDGs",
    "home.about.title": "About Palsustain",
    "home.about.description": "Palsustain is a youth-led digital initiative amplifying stories of sustainability and resilience from Palestinian communities through the lens of the Sustainable Development Goals (SDGs).",
    "home.about.mission": "Our mission is to showcase Palestinian contributions to sustainability despite challenging circumstances.",
    "home.founders.title": "Our Founders",
    "home.founders.shahd": "Shahd is passionate about sustainable development and community resilience.",
    "home.founders.aseel": "Aseel focuses on digital storytelling and environmental advocacy.",
    "home.sdgs.title": "Sustainable Development Goals",
    "home.sdgs.subtitle": "Explore the 17 SDGs through Palestinian stories",
    "home.stories.title": "Featured Stories",
    "home.stories.subtitle": "Discover stories of resilience and sustainability",
    "home.stories.readmore": "Read More",
    
    // SDG Page
    "sdg.stories.title": "Stories Related to",
    "sdg.context.title": "Palestinian Context",
    "sdg.related.title": "Related SDGs",
    
    // Media Page
    "media.title": "Media Gallery",
    "media.subtitle": "Visual storytelling from Palestine",
    "media.filter.all": "All",
    "media.filter.infographics": "Infographics",
    "media.filter.photos": "Photos",
    "media.filter.videos": "Videos",
    
    // Journey Page
    "journey.title": "Our Journey",
    "journey.subtitle": "The story behind Palsustain",
    
    // Footer
    "footer.rights": "All Rights Reserved",
    "footer.follow": "Follow Us",
  },
  ar: {
    // Navigation
    "nav.home": "الرئيسية",
    "nav.about": "عن المبادرة",
    "nav.sdgs": "أهداف التنمية المستدامة",
    "nav.stories": "القصص",
    "nav.media": "الوسائط",
    "nav.journey": "رحلتنا",
    "nav.contact": "اتصل بنا",
    
    // Home page
    "home.hero.title": "قصص الاستدامة من فلسطين",
    "home.hero.subtitle": "تضخيم مرونة الفلسطينيين من خلال عدسة أهداف التنمية المستدامة",
    "home.hero.cta": "استكشف الأهداف",
    "home.about.title": "عن بالسستين",
    "home.about.description": "بالسستين هي مبادرة رقمية بقيادة الشباب تضخم قصص الاستدامة والمرونة من المجتمعات الفلسطينية من خلال عدسة أهداف التنمية المستدامة.",
    "home.about.mission": "مهمتنا هي إظهار المساهمات الفلسطينية في الاستدامة على الرغم من الظروف الصعبة.",
    "home.founders.title": "المؤسسون",
    "home.founders.shahd": "شهد متحمسة للتنمية المستدامة ومرونة المجتمع.",
    "home.founders.aseel": "أسيل تركز على رواية القصص الرقمية والدعوة البيئية.",
    "home.sdgs.title": "أهداف التنمية المستدامة",
    "home.sdgs.subtitle": "استكشف أهداف التنمية المستدامة السبعة عشر من خلال القصص الفلسطينية",
    "home.stories.title": "قصص مميزة",
    "home.stories.subtitle": "اكتشف قصص المرونة والاستدامة",
    "home.stories.readmore": "اقرأ المزيد",
    
    // SDG Page
    "sdg.stories.title": "القصص المتعلقة بـ",
    "sdg.context.title": "السياق الفلسطيني",
    "sdg.related.title": "أهداف ذات صلة",
    
    // Media Page
    "media.title": "معرض الوسائط",
    "media.subtitle": "سرد القصص البصري من فلسطين",
    "media.filter.all": "الكل",
    "media.filter.infographics": "الرسوم البيانية",
    "media.filter.photos": "الصور",
    "media.filter.videos": "الفيديو",
    
    // Journey Page
    "journey.title": "رحلتنا",
    "journey.subtitle": "القصة وراء بالسستين",
    
    // Footer
    "footer.rights": "جميع الحقوق محفوظة",
    "footer.follow": "تابعنا",
  }
};

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const [language, setLanguage] = useState<Language>(() => {
    // Check local storage or browser language
    const savedLanguage = localStorage.getItem("language") as Language;
    if (savedLanguage && (savedLanguage === "en" || savedLanguage === "ar")) {
      return savedLanguage;
    }
    // Check browser language
    const browserLang = navigator.language.split("-")[0];
    return browserLang === "ar" ? "ar" : "en";
  });
  
  useEffect(() => {
    // Update document direction and language
    document.documentElement.lang = language;
    document.documentElement.dir = language === "ar" ? "rtl" : "ltr";
    document.body.className = language === "ar" ? "rtl" : "";
    localStorage.setItem("language", language);
  }, [language]);

  // Translation function
  const t = (key: string): string => {
    return translations[language][key as keyof typeof translations[typeof language]] || key;
  };

  const value = {
    language,
    setLanguage,
    t
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};
